import * as React from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";

/**
 * Searchable Select Component
 * Allows searching through a large list of options
 */
export function SearchableSelect({
  value,
  onValueChange,
  options = [],
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  icon: Icon,
  className,
  triggerClassName,
  "data-testid": testId,
}) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");

  // Find the selected option's label
  const selectedOption = options.find((opt) => 
    (opt.value || opt.code || opt.store_id || opt) === value
  );
  const selectedLabel = selectedOption?.label || selectedOption?.name || selectedOption?.store_name || selectedOption || placeholder;

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!searchValue) return options;
    const search = searchValue.toLowerCase();
    return options.filter((opt) => {
      const label = opt.label || opt.name || opt.store_name || opt;
      return label.toLowerCase().includes(search);
    });
  }, [options, searchValue]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-[200px] justify-between bg-transparent border-input hover:bg-accent/50",
            triggerClassName
          )}
          data-testid={testId}
        >
          <span className="flex items-center gap-2 truncate">
            {Icon && <Icon className="h-4 w-4 shrink-0 opacity-70" />}
            <span className="truncate">{value && value !== "all" ? selectedLabel : placeholder}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0 bg-gray-900 border-gray-700" align="start">
        <Command className="bg-transparent">
          <div className="flex items-center border-b border-gray-700 px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <CommandList className="max-h-[300px] overflow-y-auto">
            <CommandEmpty className="py-6 text-center text-sm text-gray-400">
              {emptyText}
            </CommandEmpty>
            <CommandGroup>
              {/* All option */}
              <CommandItem
                value="all"
                onSelect={() => {
                  onValueChange("all");
                  setOpen(false);
                  setSearchValue("");
                }}
                className="cursor-pointer hover:bg-gray-800"
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === "all" ? "opacity-100" : "opacity-0"
                  )}
                />
                {placeholder}
              </CommandItem>
              
              {/* Filtered options */}
              {filteredOptions.slice(0, 100).map((opt, idx) => {
                const optValue = opt.value || opt.code || opt.store_id || opt;
                const optLabel = opt.label || opt.name || opt.store_name || opt;
                
                return (
                  <CommandItem
                    key={`${optValue}-${idx}`}
                    value={optLabel}
                    onSelect={() => {
                      onValueChange(optValue);
                      setOpen(false);
                      setSearchValue("");
                    }}
                    className="cursor-pointer hover:bg-gray-800"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === optValue ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="truncate">{optLabel}</span>
                  </CommandItem>
                );
              })}
              
              {filteredOptions.length > 100 && (
                <div className="py-2 px-3 text-xs text-gray-500 text-center">
                  Showing 100 of {filteredOptions.length} results. Type to search more.
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableSelect;
